import axios from 'axios'
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

import { logger } from '@/lib/logger'

import { useAuthStore } from '@/stores/auth'
import { useLanguageStore } from '@/stores/language'

import { BASE_API_URLS } from '@/config/api'

import toast from '@/components/toast'

import { useBranchStore } from '../stores/useBranchStore'

// Extend AxiosRequestConfig to recognize custom properties
declare module 'axios' {
  interface AxiosRequestConfig {
    isRoot?: boolean
    skipLanguageHeader?: boolean
  }
}

const API_ROOT_URL = BASE_API_URLS.root
const API_VERSIONED_URL = BASE_API_URLS.versioned

const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
}

const apiClient = axios.create({
  headers: defaultHeaders,
  withCredentials: true,
  withXSRFToken: true,
})

// Request interceptor
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { activeBranch } = useBranchStore.getState()
  // Set base URL
  config.baseURL = config.isRoot ? API_ROOT_URL : API_VERSIONED_URL

  // Get current language from Zustand store (without React hook)
  const currentLanguage = useLanguageStore.getState().language

  // Add language header (unless explicitly skipped)
  if (!config.skipLanguageHeader) {
    config.headers = config.headers || {}
    config.headers['X-Locale'] = currentLanguage
  }

  if (activeBranch) {
    if (activeBranch.id === 'all') {
      // "All branches", don't send the header
      delete config.headers['X-Branch-ID']
    } else {
      config.headers['X-Branch-ID'] = activeBranch.id
    }
  }

  // If sending FormData, remove Content-Type header to let browser set it with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }

  // CDN Cache Control: Skip cache for mutations and auth endpoints
  const shouldSkipCache =
    config.method !== 'get' ||
    config.url?.includes('/auth/') ||
    config.url?.includes('/sanctum/') ||
    config.url?.includes('/my-account/') ||
    config.url?.includes('/user/') ||
    config.data instanceof FormData

  if (shouldSkipCache) {
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
    config.headers['Pragma'] = 'no-cache'
    config.headers['Expires'] = '0'
  }

  return config
})

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,

  (error: AxiosError) => {
    const status = error.response?.status

    // Handle 503 (Maintenance Mode) - when CORS headers are present
    if (status === 503) {
      const maintenanceData = error.response?.data as any
      const message = maintenanceData?.message || 'System under maintenance'
      const retryAfter = maintenanceData?.retry_after || error.response?.headers['retry-after']

      logger.warn('Maintenance mode detected')

      // Dynamically import to avoid circular dependency
      import('@/stores/useMaintenanceStore').then(({ useMaintenanceStore }) => {
        useMaintenanceStore.getState().set({
          isMaintenance: true,
          message,
          retryAfter: retryAfter ? parseInt(String(retryAfter), 10) : null,
          lastChecked: new Date(),
        })
      })

      return Promise.reject(error)
    }

    // Handle 401 (Unauthorized)
    if (status === 401) {
      const { user, logout, setUser } = useAuthStore.getState()

      if (user) {
        toast.error('Session expired!')
        logout() // clear token, storage, etc.
      }

      setUser(null)
      // Don't log 401s - they're expected when sessions expire
      return Promise.reject(error)
    }

    // Log all other API errors with full context
    const endpoint = error.config?.url || 'unknown endpoint'
    logger.apiError(`API request failed: ${endpoint}`, error)

    return Promise.reject(error)
  }
)

// Helper functions (updated to handle language automatically)
export async function getCsrfCookie(): Promise<boolean> {
  try {
    await apiClient.get('/api/csrf-cookie', { isRoot: true })
    return true
  } catch (error) {
    return false
  }
}

export async function getTestOopSpam(): Promise<boolean> {
  return false
}

export default apiClient
